'use client';

import { produce } from 'immer';
import { FC, ReactNode, useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import {
  CustomerDocumentRequestResponse,
  isApprovalRequiringAction,
} from '@/data/DocumentUploadApi/DocumentUploadApi.types';
import { BaseCarData } from '@/types/Car.types';
import { OrdersResponse } from '@/types/CataloguePage.types';

import { storeFactory } from './storeFactory';

interface DocumentStoreStateData {
  documentRequests: CustomerDocumentRequestResponse[];
  documentRequestsLoading: boolean;
  hasPendingDocuments: boolean;
  documentCarData: BaseCarData | null;
  documentOrderData: OrdersResponse | null;
}

interface DocumentStoreStateFunctions {
  setDocumentRequestsLoading: () => void;
  setDocumentRequests: (documentRequests: CustomerDocumentRequestResponse[]) => void;
  setDocumentOrderData: (orderData: OrdersResponse, carData: BaseCarData | null) => void;
  clearDocumentData: () => void;
}

/**
 * Check if there are any document approvals that need action (pending or awaiting_submission; not submitted)
 */
const checkHasPendingDocuments = (documentRequests: CustomerDocumentRequestResponse[]): boolean => {
  return documentRequests.some((request) =>
    request.customer_document_approvals?.some((approval) => isApprovalRequiringAction(approval.status)),
  );
};

const {
  store: documentStore,
  StoreProvider: DocumentStoreProvider,
  useCreateStore: useCreateDocumentStore,
} = storeFactory<DocumentStoreStateData, DocumentStoreStateFunctions>(
  () => ({
    documentRequests: [],
    documentRequestsLoading: false,
    hasPendingDocuments: false,
    documentCarData: null,
    documentOrderData: null,
  }),
  (set) => ({
    setDocumentRequestsLoading: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.documentRequestsLoading = true;
        }),
      );
    },
    setDocumentRequests: (documentRequests: CustomerDocumentRequestResponse[]) => {
      set((state) =>
        produce(state, (draft) => {
          draft.documentRequests = documentRequests;
          draft.documentRequestsLoading = false;
          draft.hasPendingDocuments = checkHasPendingDocuments(documentRequests);
        }),
      );
    },
    setDocumentOrderData: (orderData: OrdersResponse, carData: BaseCarData | null) => {
      set((state) =>
        produce(state, (draft) => {
          draft.documentOrderData = orderData;
          draft.documentCarData = carData;
        }),
      );
    },
    clearDocumentData: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.documentRequests = [];
          draft.documentRequestsLoading = false;
          draft.hasPendingDocuments = false;
          draft.documentCarData = null;
          draft.documentOrderData = null;
        }),
      );
    },
  }),
);

export { documentStore, DocumentStoreProvider, useCreateDocumentStore };

export const DocumentStore: FC<{ children: ReactNode }> = ({ children }) => {
  const createDocumentStore = useCreateDocumentStore({});

  return <DocumentStoreProvider createStore={createDocumentStore}>{children}</DocumentStoreProvider>;
};

export const DocumentContextSetData: FC<Pick<DocumentStoreStateData, 'documentRequests'>> = ({ documentRequests }) => {
  const { setDocumentRequests } = documentStore(
    (state) => ({
      setDocumentRequests: state.setDocumentRequests,
    }),
    shallow,
  );

  useEffect(() => {
    setDocumentRequests(documentRequests);
  }, [documentRequests, setDocumentRequests]);

  return null;
};
